import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { emit } from "@/lib/events";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Login() {
  const { login, register, otpLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [otpForm, setOtpForm] = useState({ email: "", otp: "", name: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const finish = (user) => {
    toast.success(`Welcome, ${user.name}`);
    navigate(user.role === "admin" && next === "/account" ? "/admin" : next);
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      finish(await login(loginForm.email, loginForm.password));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(regForm.name, regForm.email, regForm.password, regForm.phone);
      emit("sign_up", { method: "email" }, { email: u.email, phone: u.phone });
      finish(u);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const requestOtp = async () => {
    if (!/^\S+@\S+\.\S+$/.test(otpForm.email)) { toast.error("Enter a valid email address"); return; }
    try {
      const r = await api.post("/auth/otp/request", { identifier: otpForm.email });
      setOtpSent(true);
      setResendIn(30);
      if (r.data?.dev_otp) { setDevOtp(r.data.dev_otp); toast.info(`Test mode — your code is ${r.data.dev_otp}`); }
      else toast.success(`Code sent to ${otpForm.email}`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      finish(await otpLogin(otpForm.email, otpForm.otp, otpForm.name));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="container-x py-14 max-w-md mx-auto" data-testid="login-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-center">Your Account</h1>
      <p className="text-neutral-500 text-sm text-center mt-1">Sign in, create an account, or use a one-time email code.</p>
      <Tabs defaultValue="login" className="mt-8">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">Sign Up</TabsTrigger>
          <TabsTrigger value="otp" data-testid="tab-otp">Email OTP</TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <form onSubmit={doLogin} className="space-y-4 border border-neutral-200 p-6 mt-2">
            <div><Label htmlFor="li-email">Email</Label><Input id="li-email" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} data-testid="login-email-input" /></div>
            <div><Label htmlFor="li-pass">Password</Label><Input id="li-pass" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} data-testid="login-password-input" /></div>
            <Button type="submit" className="w-full h-11 bg-neutral-900 hover:bg-brand text-white btn-press" disabled={busy} data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="register">
          <form onSubmit={doRegister} className="space-y-4 border border-neutral-200 p-6 mt-2">
            <div><Label htmlFor="rg-name">Full Name</Label><Input id="rg-name" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} data-testid="register-name-input" /></div>
            <div><Label htmlFor="rg-email">Email</Label><Input id="rg-email" type="email" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} data-testid="register-email-input" /></div>
            <div><Label htmlFor="rg-phone">Phone (optional)</Label><Input id="rg-phone" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} data-testid="register-phone-input" /></div>
            <div><Label htmlFor="rg-pass">Password</Label><Input id="rg-pass" type="password" required minLength={6} value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} data-testid="register-password-input" /></div>
            <Button type="submit" className="w-full h-11 bg-neutral-900 hover:bg-brand text-white btn-press" disabled={busy} data-testid="register-submit-btn">
              {busy ? "Creating…" : "Create Account"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="otp">
          <div className="space-y-4 border border-neutral-200 p-6 mt-2">
            <div><Label htmlFor="otp-email">Email</Label><Input id="otp-email" type="email" value={otpForm.email} onChange={(e) => setOtpForm({ ...otpForm, email: e.target.value.trim().toLowerCase() })} placeholder="you@example.com" data-testid="otp-email-input" /></div>
            {otpSent && (
              <>
                <p className="text-[11px] text-neutral-500">Code sent — or tap the <strong>sign-in link</strong> in the same email. Check spam if it hides.</p>
                <div><Label htmlFor="otp-name">Name (first time)</Label><Input id="otp-name" value={otpForm.name} onChange={(e) => setOtpForm({ ...otpForm, name: e.target.value })} data-testid="otp-name-input" /></div>
                <div><Label htmlFor="otp-code">Code</Label><Input id="otp-code" value={otpForm.otp} onChange={(e) => setOtpForm({ ...otpForm, otp: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="6-digit code" data-testid="otp-code-input" /></div>
              </>
            )}
            {!otpSent ? (
              <Button onClick={requestOtp} className="w-full h-11 bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="otp-request-btn">Email me a sign-in code</Button>
            ) : (
              <div className="flex gap-2">
                <Button onClick={verifyOtp} disabled={busy} className="flex-1 h-11 bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="otp-verify-btn">
                  {busy ? "Verifying…" : "Verify & Sign In"}
                </Button>
                <Button variant="outline" onClick={requestOtp} disabled={resendIn > 0} className="h-11" data-testid="otp-resend-btn">
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
                </Button>
              </div>
            )}
            <p className="text-[11px] text-neutral-400 font-mono">{devOtp ? `TEST MODE — EMAIL STUB. CURRENT CODE: ${devOtp}` : "PASSWORDLESS — CODE + MAGIC LINK BY EMAIL"}</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
